import BackButton from '@/components/ui/BackButton'
import { useProductVariationQuery } from '@/services/products'
import { borderRadius, colors } from '@/theme'
import { getPrescriptionType } from '@/types/product'
import { Box, Chip, CircularProgress, Divider, Grid, Paper, Typography } from '@mui/material'
import { useParams } from 'react-router-dom'

interface FieldDisplayProps {
  label: string
  value: string | number | undefined
}

function FieldDisplay({ label, value }: FieldDisplayProps) {
  return (
    <Box>
      <Typography
        variant='fieldLabel'
        sx={{ display: 'block', mb: 1, color: colors.text.dark, fontWeight: 600, fontSize: 14 }}
      >
        {label}
      </Typography>
      <Typography sx={{ color: colors.text.secondary, fontSize: 14 }}>{value ?? '-'}</Typography>
    </Box>
  )
}

function FieldDisplayHtml({ label, html }: { label: string; html: string }) {
  return (
    <Box>
      <Typography
        variant='fieldLabel'
        sx={{ display: 'block', mb: 1, color: colors.text.dark, fontWeight: 600, fontSize: 14 }}
      >
        {label}
      </Typography>
      {html?.trim() ? (
        <Box
          sx={{
            color: colors.text.secondary,
            fontSize: 14,
            '& ul': { pl: 2, my: 0.5 },
            '& li': { mb: 0.25 },
            '& strong': { color: colors.text.dark }
          }}
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Product description from API, rendered as HTML
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <Typography sx={{ color: colors.text.secondary, fontSize: 14 }}>-</Typography>
      )}
    </Box>
  )
}

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: product, isLoading, isError } = useProductVariationQuery(id)

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError || !product) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Product not found</Typography>
      </Box>
    )
  }

  const singleVariant = product.variations?.length === 1 ? product.variations[0] : null
  const hasMultipleVariants = (product.variations?.length ?? 0) > 1
  const displayName = singleVariant?.title || product.title

  const price = singleVariant?.price != null ? singleVariant.price : product.price
  const priceFormatted = price != null ? `£${(price / 100).toFixed(2)}` : '-'
  const inStock = singleVariant?.in_stock ?? product.in_stock
  const categoriesText = product.categories?.length ? product.categories.join(', ') : '-'
  const createdDate =
    product.created && product.created > 0 ? new Date(product.created * 1000).toLocaleDateString() : '-'

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <BackButton fallbackPath='/products' defaultLabel='Back to Products' />
        <Typography variant='pageTitle' sx={{ fontSize: 24 }}>
          {displayName}
        </Typography>
      </Box>

      {/* Main product info */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: `1px solid ${colors.border.default}`,
          borderRadius: borderRadius.lg
        }}
      >
        <Grid container spacing={4}>
          {/* Row 1 */}
          <Grid size={3}>
            <FieldDisplay label='Product Name' value={displayName} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='Strength' value={singleVariant?.strength ?? '-'} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='SKU' value={singleVariant?.sku ?? product.sku} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='Prescription Type' value={getPrescriptionType(product.product_group)} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='Price' value={priceFormatted} />
          </Grid>

          {/* Row 2 */}
          <Grid size={3}>
            <FieldDisplay label='Categories' value={categoriesText} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='In Stock' value={inStock ? 'Yes' : 'No'} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='POM' value={product.is_pom ? 'Yes' : 'No'} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay label='Reorderable' value={product.is_reorderable ? 'Yes' : 'No'} />
          </Grid>

          {/* Row 3 */}
          <Grid size={3}>
            <FieldDisplay label='Created' value={createdDate} />
          </Grid>
          <Grid size={3}>
            <FieldDisplay
              label='Reviews'
              value={
                product.reviews?.count != null && product.reviews.count > 0
                  ? `${product.reviews.average} avg (${product.reviews.count} reviews)`
                  : product.reviews?.count === 0
                    ? 'No reviews'
                    : '-'
              }
            />
          </Grid>
          <Grid size={3}>
            <FieldDisplay
              label='Weight'
              value={product.weight != null && product.weight > 0 ? `${Math.round(product.weight * 1000)} g` : '-'}
            />
          </Grid>

          {/* Description - rendered as HTML */}
          <Grid size={12}>
            <Box
              sx={{
                mb: 2,
                p: 2,
                border: `1px solid ${colors.border.default}`,
                borderRadius: borderRadius.md,
                backgroundColor: colors.background.default
              }}
            >
              <FieldDisplayHtml label='Description' html={product.description_full || product.description || ''} />
            </Box>
          </Grid>
        </Grid>

        {/* Variations - only when multiple */}
        {hasMultipleVariants && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 3 }}>Variations</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {product.variations.map(v => (
                <Paper
                  key={v.hash}
                  variant='outlined'
                  sx={{
                    p: 2,
                    borderRadius: borderRadius.md,
                    border: `1px solid ${colors.border.default}`
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid size={3}>
                      <FieldDisplay label='Variant' value={v.title} />
                    </Grid>
                    <Grid size={2}>
                      <FieldDisplay label='SKU' value={v.sku} />
                    </Grid>
                    <Grid size={2}>
                      <FieldDisplay label='Strength' value={v.strength || '-'} />
                    </Grid>
                    <Grid size={2}>
                      <FieldDisplay label='Price' value={v.price != null ? `£${(v.price / 100).toFixed(2)}` : '-'} />
                    </Grid>
                    <Grid size={2}>
                      <Chip
                        label={v.in_stock ? 'In Stock' : 'Out of Stock'}
                        size='small'
                        sx={{
                          bgcolor: v.in_stock ? colors.status.successLight : colors.status.errorLight,
                          color: v.in_stock ? colors.status.success : colors.status.error,
                          fontWeight: 500,
                          fontSize: 12
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  )
}
